import React, { useState } from 'react'
import api from '../services/api'

interface FichaViagem {
  ficcod: number
  ficdats: string | null
  ficdatr: string | null
  destino: string
  local: string
  veiculo: string | null
  ficconf: string
}

interface Props {
  cnpj: string
  onClose: () => void
  onSelect: (ficcod: number) => void
}

function fmtData(iso: string | null): string {
  if (!iso) return '—'
  return iso.slice(0, 10).split('-').reverse().join('/')
}

// Situação da ficha (frota.ficvia.ficconf); letra sem nome conhecido aparece como está
const SITUACAO: Record<string, { nome: string; cor: string; fundo: string }> = {
  U: { nome: 'Concluída', cor: '#166534', fundo: '#dcfce7' },
  X: { nome: 'Cancelada', cor: '#991b1b', fundo: '#fee2e2' },
}

function hoje(): string {
  return new Date().toISOString().slice(0, 10)
}

function haUmMes(): string {
  const d = new Date()
  d.setMonth(d.getMonth() - 1)
  return d.toISOString().slice(0, 10)
}

export default function FichaViagemModal({ cnpj, onClose, onSelect }: Props) {
  const [inicio, setInicio] = useState(haUmMes())
  const [fim, setFim] = useState(hoje())
  const [lista, setLista] = useState<FichaViagem[]>([])
  const [carregando, setCarregando] = useState(false)
  const [buscou, setBuscou] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  function buscar() {
    setCarregando(true)
    setErro(null)
    api.get('/cte-os/fichas-viagem', { params: { cnpj, inicio, fim } })
      .then(r => { setLista(r.data); setBuscou(true) })
      .catch(e => setErro(e?.response?.data?.error ?? 'Erro ao buscar fichas de viagem'))
      .finally(() => setCarregando(false))
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" style={{ width: 1180, maxWidth: '96vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Fichas de Viagem do Cliente</h2>
          <button className="modal-fechar" onClick={onClose}>✕</button>
        </div>

        <div style={{ padding: '0.75rem 1rem 0.5rem', display: 'flex', gap: '0.6rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div className="field" style={{ marginBottom: 0, flex: '0 0 150px' }}>
            <label>De</label>
            <input type="date" value={inicio} onChange={e => setInicio(e.target.value)} />
          </div>
          <div className="field" style={{ marginBottom: 0, flex: '0 0 150px' }}>
            <label>Até</label>
            <input type="date" value={fim} onChange={e => setFim(e.target.value)} />
          </div>
          <button className="btn btn-primary btn-sm" onClick={buscar} disabled={carregando}>
            {carregando ? 'Buscando...' : 'Buscar'}
          </button>
        </div>

        {erro && (
          <div style={{ padding: '0 1rem 0.5rem', color: '#dc2626', fontSize: '0.85rem' }}>{erro}</div>
        )}

        <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '0 1rem 1rem' }}>
          {!buscou ? (
            <p style={{ color: '#6b7280', textAlign: 'center', marginTop: '1rem' }}>Escolha o período e clique em Buscar.</p>
          ) : carregando ? (
            <p style={{ color: '#6b7280', textAlign: 'center', marginTop: '1rem' }}>Carregando...</p>
          ) : lista.length === 0 ? (
            <p style={{ color: '#6b7280', textAlign: 'center', marginTop: '1rem' }}>Nenhuma ficha de viagem encontrada para este cliente no período.</p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: 70 }} /><col style={{ width: 100 }} /><col style={{ width: 100 }} />
                <col /><col /><col style={{ width: 100 }} /><col style={{ width: 110 }} />
              </colgroup>
              <thead>
                <tr style={{ background: '#f1f5f9' }}>
                  <th style={{ padding: '0.45rem 0.6rem', textAlign: 'left', borderBottom: '1px solid #e2e8f0', color: '#374151' }}>Nº</th>
                  <th style={{ padding: '0.45rem 0.6rem', textAlign: 'left', borderBottom: '1px solid #e2e8f0', color: '#374151' }}>Saída</th>
                  <th style={{ padding: '0.45rem 0.6rem', textAlign: 'left', borderBottom: '1px solid #e2e8f0', color: '#374151' }}>Retorno</th>
                  <th style={{ padding: '0.45rem 0.6rem', textAlign: 'left', borderBottom: '1px solid #e2e8f0', color: '#374151' }}>Local de saída</th>
                  <th style={{ padding: '0.45rem 0.6rem', textAlign: 'left', borderBottom: '1px solid #e2e8f0', color: '#374151' }}>Destino</th>
                  <th style={{ padding: '0.45rem 0.6rem', textAlign: 'left', borderBottom: '1px solid #e2e8f0', color: '#374151' }}>Veículo</th>
                  <th style={{ padding: '0.45rem 0.6rem', textAlign: 'left', borderBottom: '1px solid #e2e8f0', color: '#374151' }}>Situação</th>
                </tr>
              </thead>
              <tbody>
                {lista.map(f => (
                  <tr
                    key={f.ficcod}
                    onClick={() => onSelect(f.ficcod)}
                    style={{ cursor: 'pointer', borderBottom: '1px solid #f1f5f9' }}
                    onMouseEnter={e => (e.currentTarget.style.background = '#fffbeb')}
                    onMouseLeave={e => (e.currentTarget.style.background = '')}
                  >
                    <td style={{ padding: '0.45rem 0.6rem', fontWeight: 700, color: '#92400e' }}>{f.ficcod}</td>
                    <td style={{ padding: '0.45rem 0.6rem' }}>{fmtData(f.ficdats)}</td>
                    <td style={{ padding: '0.45rem 0.6rem' }}>{fmtData(f.ficdatr)}</td>
                    <td style={{ padding: '0.45rem 0.6rem', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={f.local}>{f.local || '—'}</td>
                    <td style={{ padding: '0.45rem 0.6rem', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={f.destino}>{f.destino || '—'}</td>
                    <td style={{ padding: '0.45rem 0.6rem' }}>{f.veiculo || '—'}</td>
                    <td style={{ padding: '0.45rem 0.6rem' }}>
                      {(() => {
                        const letra = (f.ficconf || '').trim()
                        const s = SITUACAO[letra]
                        return (
                          <span style={{ background: s?.fundo ?? '#e5e7eb', color: s?.cor ?? '#374151', fontWeight: 700, fontSize: '0.75rem', borderRadius: 4, padding: '2px 8px', whiteSpace: 'nowrap' }}>
                            {s?.nome ?? (letra || '—')}
                          </span>
                        )
                      })()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  )
}
